<template>
  <div>
    <LoadingSpinner v-if="isLoading" />
    <div
      v-else
      class="customer-update"
    >
      <PageHeader title="Edit Customer" />

      <div class="shadow p-5 mt-2 mb-5 bg-white rounded">
        <CustomerForm
          :model="customer"
          :save-function="updateCustomer"
          :is-update="true"
          @cancel="$router.back()"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import CustomerForm from '@/modules/Customer/components/CustomerForm.vue'
import { useHead } from '@unhead/vue'
import { Customer } from '@/models/customer'
import { useRoute, useRouter } from 'vue-router'
import CustomerService from '@/services/CustomerService'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
useHead({ title: 'Edit Customer' })

const $route = useRoute()
const $router = useRouter()
const { notify } = useNotification()
const customerService = new CustomerService()
const queryClient = useQueryClient()

const customerId = $route.params.id as string || ''

const { data: customer, isLoading } = useQuery({
  queryKey: ['customer', customerId],
  queryFn: () => customerService.find(customerId)
})

const { mutateAsync: updateCustomer } = useMutation({
  mutationFn: (customer: Customer) => customerService.update(customerId, customer),
  onSuccess: (updatedCustomer: Customer) => {
    queryClient.setQueryData(['customer', updatedCustomer.id], updatedCustomer)
    queryClient.invalidateQueries({ queryKey: ['customers'] })

    if (updatedCustomer.isCompany) {
      notify({
        type: 'success',
        title: 'Company updated',
        text: 'Company updated successfully'
      })
    } else {
      notify({
        type: 'success',
        title: 'Customer updated',
        text: 'Customer updated successfully'
      })
    }

    $router.push({ name: 'ShowCustomer', params: { id: updatedCustomer.id } })
  },
  onError: (error) => {
    const notifyOptions = {
      type: 'error',
      title: 'Error',
      text: ''
    }
    if (customer.value.isCompany) {
      notifyOptions.text = error.message || 'An error occurred while trying to update the company'
    } else {
      notifyOptions.text = error.message || 'An error occurred while trying to update the customer'
    }
    notify(notifyOptions)
  }
})
</script>
